import { Component, OnInit } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { RouterModule } from '@angular/router';
import { AuthService } from '../../../core/services/auth.service';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ChangelogDialogComponent } from '../changelog-dialog/changelog-dialog.component';

@Component({
  selector: 'app-footer',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, RouterModule],
  providers: [DialogService],
  template: `
    <footer class="footer" *ngIf="!(isAuthenticated$ | async)">
      <div class="footer-container">
        <!-- Footer per utenti non autenticati (landing) -->
        <div class="footer-landing">
          <div class="footer-content">
            <div class="footer-brand">
              <img
                ngSrc="assets/loghi/agriflow_logo_orizzontale_TRASPARENTE_optimized.png"
                alt="AgriFlow"
                class="footer-logo"
                width="180"
                height="120"
              />
              <p class="footer-description">
                Innovazione e digitalizzazione per l'agricoltura di domani
              </p>
            </div>

            <div class="footer-links">
              <div class="footer-section">
                <h4>Prodotto</h4>
                <a (click)="scrollToSection('.features-section')"
                  >Funzionalità</a
                >
                <a (click)="scrollToSection('.cta-section')">Prezzi</a>
                <a href="#">Demo</a>
              </div>

              <div class="footer-section">
                <h4>Azienda</h4>
                <a href="#">Chi Siamo</a>
                <a href="#">Contatti</a>
                <a href="#">Blog</a>
              </div>

              <div class="footer-section">
                <h4>Supporto</h4>
                <a href="#">Documentazione</a>
                <a href="#">FAQ</a>
                <a href="#">Assistenza</a>
              </div>
            </div>
          </div>

          <div class="footer-bottom">
            <div class="footer-left">
              <p class="footer-copyright">
                © {{ currentYear }} AgriFlow. Tutti i diritti riservati.
              </p>
              <div
                class="version-badge"
                [title]="getVersionTooltip()"
                (click)="openChangelog()"
              >
                <span class="version-icon">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M12 2L2 7L12 12L22 7L12 2Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M2 17L12 22L22 17"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M2 12L12 17L22 12"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </span>
                <span class="version-text">v{{ appVersion }}</span>
                <span class="version-env">{{ environmentName }}</span>
              </div>
            </div>
            <div class="footer-legal">
              <a href="#">Privacy Policy</a>
              <a href="#">Termini di Servizio</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  `,
  styles: [
    `
      .footer {
        background: var(--surface-color);
        border-top: 1px solid var(--border-color);
        margin-top: auto;
      }

      .footer-container {
        max-width: 1200px;
        margin: 0 auto;
        padding: 0 2rem;
      }

      /* Footer Landing (utenti non autenticati) */
      .footer-landing {
        padding: 3rem 0 1rem;
      }

      .footer-content {
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 3rem;
        margin-bottom: 2rem;
      }

      .footer-brand {
        display: flex;
        flex-direction: column;
        gap: 1rem;
      }

      .footer-logo {
        height: auto;
        width: 45px;
        max-height: 30px;
        object-fit: contain;
      }

      .footer-description {
        color: var(--text-color-secondary);
        font-size: 0.9rem;
        line-height: 1.5;
        margin: 0;
      }

      .footer-links {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;
      }

      .footer-section h4 {
        color: var(--text-color);
        font-size: 1rem;
        font-weight: 600;
        margin: 0 0 1rem 0;
      }

      .footer-section a {
        display: block;
        color: var(--text-color-secondary);
        text-decoration: none;
        font-size: 0.875rem;
        margin-bottom: 0.5rem;
        transition: color 0.3s ease;
        cursor: pointer;
      }

      .footer-section a:hover {
        color: var(--primary-color);
      }

      .footer-bottom {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-top: 2rem;
        border-top: 1px solid var(--border-color);
      }

      .footer-left {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex-wrap: wrap;
      }

      .footer-copyright {
        margin: 0;
        color: var(--text-color-secondary);
        font-size: 0.875rem;
      }

      /* Version Badge */
      .version-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.375rem 0.75rem;
        background: linear-gradient(
          135deg,
          rgba(99, 102, 241, 0.1) 0%,
          rgba(168, 85, 247, 0.1) 100%
        );
        border: 1px solid rgba(99, 102, 241, 0.2);
        border-radius: 12px;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        position: relative;
        overflow: hidden;
      }

      .version-badge::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          rgba(255, 255, 255, 0.1),
          transparent
        );
        transition: left 0.5s ease;
      }

      .version-badge:hover {
        transform: translateY(-2px);
        box-shadow: 0 8px 16px rgba(99, 102, 241, 0.2);
        border-color: rgba(99, 102, 241, 0.4);
      }

      .version-badge:hover::before {
        left: 100%;
      }

      .version-icon {
        display: flex;
        align-items: center;
        color: #6366f1;
        animation: pulse 2s ease-in-out infinite;
      }

      @keyframes pulse {
        0%,
        100% {
          opacity: 1;
        }
        50% {
          opacity: 0.6;
        }
      }

      .version-text {
        font-weight: 600;
        font-size: 0.8125rem;
        background: linear-gradient(135deg, #6366f1 0%, #a855f7 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        letter-spacing: 0.02em;
      }

      .version-env {
        font-size: 0.6875rem;
        padding: 0.125rem 0.375rem;
        background: rgba(99, 102, 241, 0.15);
        border-radius: 6px;
        color: #6366f1;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .footer-legal {
        display: flex;
        gap: 2rem;
      }

      .footer-legal a {
        color: var(--text-color-secondary);
        text-decoration: none;
        font-size: 0.875rem;
        transition: color 0.3s ease;
      }

      .footer-legal a:hover {
        color: var(--primary-color);
      }

      /* Footer Dashboard (utenti autenticati) */
      .footer-dashboard {
        padding: 1rem 0;
      }

      .footer-info {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .footer-text {
        margin: 0;
        color: var(--text-color-secondary);
        font-size: 0.875rem;
      }

      .footer-actions {
        display: flex;
        gap: 1.5rem;
      }

      .footer-link {
        color: var(--text-color-secondary);
        text-decoration: none;
        font-size: 0.875rem;
        transition: color 0.3s ease;
      }

      .footer-link:hover {
        color: var(--primary-color);
      }

      /* Responsive */
      @media (max-width: 768px) {
        .footer-container {
          padding: 0 1rem;
        }

        .footer-content {
          grid-template-columns: 1fr;
          gap: 2rem;
        }

        .footer-links {
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }

        .footer-bottom {
          flex-direction: column;
          gap: 1rem;
          text-align: center;
        }

        .footer-left {
          flex-direction: column;
          align-items: center;
        }

        .footer-legal {
          gap: 1rem;
        }

        .footer-info {
          flex-direction: column;
          gap: 1rem;
          text-align: center;
        }

        .version-badge {
          font-size: 0.75rem;
          padding: 0.3rem 0.6rem;
        }
      }
    `,
  ],
})
export class FooterComponent implements OnInit {
  currentYear = new Date().getFullYear();
  isAuthenticated$: Observable<boolean>;
  appVersion = environment.version || '0.0.0';
  environmentName = environment.name;
  buildDate = new Date().toLocaleDateString('it-IT', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  private changelogDialogRef?: DynamicDialogRef;

  constructor(
    private authService: AuthService,
    private dialogService: DialogService,
  ) {
    this.isAuthenticated$ = this.authService.isAuthenticated$;
  }

  ngOnInit() {}

  scrollToSection(sectionId: string) {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  }

  getVersionTooltip(): string {
    return `Versione: ${this.appVersion}\nAmbiente: ${this.environmentName}\nData build: ${this.buildDate}\n\nClicca per vedere il changelog`;
  }

  openChangelog() {
    this.changelogDialogRef = this.dialogService.open(
      ChangelogDialogComponent,
      {
        header: '📋 Changelog AgriFlow',
        width: '70vw',
        height: '75vh',
        maximizable: false,
        modal: true,
        dismissableMask: true,
        closable: true,
        baseZIndex: 2000000,
        styleClass: 'changelog-dialog-container',
        contentStyle: {
          padding: '0',
          overflow: 'hidden',
        },
      },
    );
  }
}
